import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { PortfolioScorecard } from "@/components/PortfolioScorecard";
import { Sparkline } from "@/components/Sparkline";
import { toast } from "sonner";
import { FolderKanban, RotateCw, Trash2, ChevronLeft, TrendingUp, Plug, GitCompareArrows, ExternalLink, Bell } from "lucide-react";

const LEVEL_COLOR = {
  1: "bg-rose-500", 2: "bg-orange-400", 3: "bg-amber-400",
  4: "bg-emerald-500", 5: "bg-emerald-600",
};

export function ProgrammesList() {
  const [data, setData] = useState({ programmes: [], total: 0 });
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data: r } = await api.get("/programmes");
      setData(r);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load programmes");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Navbar />
      <main className="mx-auto max-w-6xl px-6 py-8" data-testid="programmes-page">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-violet-600 font-semibold flex items-center gap-2">
              <FolderKanban size={14} /> EKSELENS · ERIO · Saved programmes
            </p>
            <h1 className="mt-1 text-3xl font-bold tracking-tight text-ek-ink">Programmes</h1>
            <p className="mt-1 text-sm text-ek-ink/65 max-w-3xl">
              Persisted plan portfolios. Each save captures a snapshot; re-scoring appends a new snapshot so you can see direction of travel.
            </p>
          </div>
          <Link to="/plan-portfolio" data-testid="programmes-create-link"
                className="rounded-md bg-ek-purple px-3 py-2 text-sm font-semibold text-white hover:bg-ek-purple/90">
            + New portfolio
          </Link>
        </div>

        <div className="mt-6">
          {loading ? (
            <div className="rounded-xl bg-white p-12 text-center text-slate-500 ring-1 ring-slate-200" data-testid="programmes-loading">Loading…</div>
          ) : data.programmes.length === 0 ? (
            <div className="rounded-xl bg-white p-12 text-center ring-1 ring-slate-200" data-testid="programmes-empty">
              <FolderKanban size={36} className="mx-auto text-slate-300" />
              <p className="mt-2 text-sm text-slate-500">No programmes saved yet. Score a portfolio and click <strong>Save as programme</strong> to track it over time.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {data.programmes.map((p) => {
                const cur = p.current?.portfolio || {};
                const lvl = cur.overall_level || 1;
                const snaps = (p.snapshots || []).map((s) => s.overall_score);
                return (
                  <Link key={p.programme_id} to={`/programmes/${p.programme_id}`}
                        data-testid={`programme-card-${p.programme_id}`}
                        className="block rounded-xl bg-white p-5 ring-1 ring-slate-200 transition hover:ring-violet-300 hover:shadow-md">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="font-semibold text-ek-ink truncate">{p.name}</h3>
                        <p className="text-xs text-ek-ink/55 mt-0.5">
                          {cur.workstream_count || 0} workstream(s) · {cur.total_items || 0} items
                        </p>
                      </div>
                      <span className={`shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium text-white ${LEVEL_COLOR[lvl]}`}>
                        L{lvl}
                      </span>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="text-xs text-slate-600">
                        <p>Score <strong>{(cur.overall_score || 0).toFixed(2)}</strong> · Confidence {cur.confidence || "—"}</p>
                        <p className="text-[11px] text-slate-400 mt-0.5">{p.snapshots?.length || 0} snapshot(s)</p>
                      </div>
                      <Sparkline values={snaps} testid={`programme-spark-${p.programme_id}`}
                                 ariaLabel={`${p.name} trend`} />
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export function ProgrammeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rescoring, setRescoring] = useState(false);

  const fetchOne = useCallback(async () => {
    setLoading(true);
    try {
      const { data: r } = await api.get(`/programmes/${id}`);
      setDoc(r);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load programme");
    } finally { setLoading(false); }
  }, [id]);
  useEffect(() => { fetchOne(); }, [fetchOne]);

  const rescore = async () => {
    setRescoring(true);
    try {
      const { data: r } = await api.post(`/programmes/${id}/rescore`);
      setDoc(r);
      toast.success(`Re-scored — L${r.current.portfolio.overall_level} (${r.snapshots.length} snapshots)`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Re-score failed");
    } finally { setRescoring(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete programme '${doc?.name}'? This cannot be undone.`)) return;
    try {
      await api.delete(`/programmes/${id}`);
      toast.success("Programme deleted");
      navigate("/programmes");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-ek-canvas">
        <Navbar />
        <main className="mx-auto max-w-6xl px-6 py-8">
          <div className="rounded-xl bg-white p-12 text-center text-slate-500 ring-1 ring-slate-200" data-testid="programme-loading">Loading…</div>
        </main>
      </div>
    );
  }
  if (!doc) return null;

  const snaps = doc.snapshots || [];
  const scores = snaps.map((s) => s.overall_score);
  const first = scores[0];
  const last = scores[scores.length - 1];
  const delta = (last - first);

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Navbar />
      <main className="mx-auto max-w-6xl px-6 py-8" data-testid="programme-detail-page">
        <Link to="/programmes" className="text-xs text-violet-700 hover:underline inline-flex items-center gap-1" data-testid="programme-back">
          <ChevronLeft size={12} /> Back to programmes
        </Link>
        <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-violet-600 font-semibold flex items-center gap-2">
              <FolderKanban size={14} /> Saved programme
            </p>
            <h1 className="mt-1 text-3xl font-bold tracking-tight text-ek-ink">{doc.name}</h1>
            {doc.notes && <p className="mt-1 text-sm text-ek-ink/60">{doc.notes}</p>}
            <p className="mt-2 text-xs text-ek-ink/50">
              Saved {new Date(doc.created_at).toLocaleString()} · Updated {new Date(doc.updated_at).toLocaleString()} · {snaps.length} snapshot(s)
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={rescore} disabled={rescoring}
                    data-testid="programme-rescore-btn"
                    className="inline-flex items-center gap-1.5 rounded-md bg-ek-purple px-3 py-2 text-sm font-semibold text-white hover:bg-ek-purple/90 disabled:opacity-50">
              <RotateCw size={14} /> {rescoring ? "Re-scoring…" : "Re-score"}
            </button>
            <button onClick={remove} data-testid="programme-delete-btn"
                    className="inline-flex items-center gap-1.5 rounded-md border border-rose-300 px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50">
              <Trash2 size={14} /> Delete
            </button>
          </div>
        </div>

        {snaps.length >= 2 && (
          <div className="mt-6 rounded-xl bg-white p-5 ring-1 ring-slate-200" data-testid="programme-trend">
            <h2 className="text-sm font-semibold text-violet-900 flex items-center gap-2">
              <TrendingUp size={14} /> Trend ({snaps.length} snapshot{snaps.length === 1 ? "" : "s"})
            </h2>
            <div className="mt-3 flex items-center gap-6">
              <Sparkline values={scores} width={220} height={48}
                         testid="programme-trend-spark" ariaLabel={`${doc.name} score trend`} />
              <div className="text-sm">
                <p className="text-ek-ink/60">First: <strong>{first?.toFixed(3)}</strong></p>
                <p className="text-ek-ink/60">Latest: <strong>{last?.toFixed(3)}</strong></p>
                <p className={`mt-1 font-semibold ${delta >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                  Delta {delta >= 0 ? "+" : ""}{delta.toFixed(3)}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6">
          <PortfolioScorecard data={doc.current} testidPrefix="pd" />
        </div>

        <DriftSection programmeId={id} />
      </main>
    </div>
  );
}

/**
 * Live-vs-uploaded drift comparison — pick a configured PPM connector,
 * score its synced plans alongside the saved programme, and surface the
 * differences (overall score delta, items only in the saved plan, items
 * only in Jira, status mismatches between matched IDs). PMO Director's
 * "where is reality drifting from the plan-of-record?" in one click.
 */
/**
 * FreshnessBadge — "Live data freshness" credibility moment. Surfaces the
 * `last_auto_sync_at` + `last_sync_count` for the currently-picked connector
 * so a viewer sees, at a glance, that the comparison is fed by live data,
 * not a stale upload. Ticks every minute so "N min ago" stays accurate
 * without a server round-trip.
 */
function FreshnessBadge({ connectors, pick }) {
  const c = connectors.find((x) => x.connector_id === pick);
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((v) => v + 1), 60_000);
    return () => clearInterval(t);
  }, []);
  if (!c) return null;
  const lastSync = c.last_auto_sync_at || c.last_sync_at;
  if (!lastSync) {
    // Configured but never synced — surface that clearly rather than hiding.
    return (
      <p data-testid="drift-freshness-badge" data-state="never-synced"
         className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-medium text-amber-800 ring-1 ring-amber-200">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500" />
        {c.name} not synced yet — open <Link to="/connectors" className="underline hover:text-amber-900">Connectors</Link> and click Sync.
      </p>
    );
  }
  const mins = Math.max(0, Math.round((Date.now() - new Date(lastSync).getTime()) / 60_000));
  const human = mins < 1 ? "just now" : mins < 60 ? `${mins} min ago` : mins < 24*60 ? `${Math.round(mins/60)}h ago` : `${Math.round(mins/(24*60))}d ago`;
  const fresh = mins < 120;     // green ≤2h
  const stale = mins > 24 * 60; // amber >24h (lowered from 48h per testing-agent suggestion)
  return (
    <p data-testid="drift-freshness-badge" data-state={stale ? "stale" : fresh ? "fresh" : "ok"}
       className={`mt-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ${
         stale ? "bg-amber-50 text-amber-800 ring-1 ring-amber-200"
               : fresh ? "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200"
                       : "bg-ek-canvas text-ek-ink/70 ring-1 ring-black/10"
       }`}
       title={`Last synced: ${new Date(lastSync).toLocaleString()}${c.last_error ? "  ·  " + c.last_error : ""}`}>
      <span className={`inline-block h-1.5 w-1.5 rounded-full ${
        stale ? "bg-amber-500" : fresh ? "bg-emerald-500" : "bg-slate-400"
      }`} />
      {stale && <strong className="mr-1" data-testid="freshness-stale">Stale ·</strong>}
      {c.name} synced {human} · {c.last_sync_count || 0} plan{c.last_sync_count === 1 ? "" : "s"} live
      {c.auto_sync_enabled && <span className="ml-1 text-ek-ink/45">· auto-sync every {c.auto_sync_cadence_min || 60}m</span>}
    </p>
  );
}


/**
 * DriftWatchControl — ambient alert opt-in for a (programme, connector) pair.
 * Click "Watch" → backend creates a drift_watch row with sensible defaults.
 * Once watched, expose threshold knobs (PIS delta floor, mismatch count
 * ceiling) so the user can tune sensitivity. Worker fires every hour; emails
 * the tenant CRO (or override) when thresholds breach. Dedup: 24h cooldown or
 * delta worsened by ≥0.1.
 */
function DriftWatchControl({ programmeId, connectorId, watches, onChanged }) {
  const existing = watches.find((w) => w.connector_id === connectorId);
  const watched = Boolean(existing?.enabled);
  const [busy, setBusy] = useState(false);
  const [thresholdDelta, setThresholdDelta] = useState(existing?.threshold_delta ?? -0.5);
  const [thresholdMm, setThresholdMm] = useState(existing?.threshold_mismatches ?? 10);

  const upsert = async (patch) => {
    setBusy(true);
    try {
      await api.put(`/programmes/${programmeId}/drift-watches/${connectorId}`, patch);
      toast.success("Drift watch updated");
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not save");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/programmes/${programmeId}/drift-watches/${connectorId}`);
      toast.success("Drift watch removed");
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not remove");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-3 rounded-lg border border-violet-100 bg-violet-50/40 px-3 py-2"
         data-testid="drift-watch-control">
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <button
          type="button"
          onClick={() => watched ? remove() : upsert({})}
          disabled={busy}
          data-testid="drift-watch-toggle"
          aria-pressed={watched}
          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-semibold transition-colors disabled:opacity-60 ${
            watched
              ? "border-emerald-400 bg-emerald-100 text-emerald-900"
              : "border-violet-300 bg-white text-violet-800 hover:bg-violet-100"
          }`}
        >
          <Bell size={11} className={watched ? "fill-emerald-700 text-emerald-700" : ""} />
          {watched ? "Watching this drift" : "Watch this drift"}
        </button>
        {watched && (
          <>
            <label className="inline-flex items-center gap-1 text-ek-ink/65">
              Alert when Δ ≤
              <input
                type="number" step="0.05" min="-5" max="5"
                value={thresholdDelta}
                onChange={(e) => setThresholdDelta(parseFloat(e.target.value))}
                onBlur={() => {
                  if (thresholdDelta !== existing.threshold_delta) {
                    upsert({ threshold_delta: thresholdDelta });
                  }
                }}
                data-testid="drift-watch-threshold-delta"
                className="w-16 rounded border border-black/10 px-1.5 py-0.5 text-center"
              />
            </label>
            <label className="inline-flex items-center gap-1 text-ek-ink/65">
              or mismatches ≥
              <input
                type="number" step="1" min="1" max="1000"
                value={thresholdMm}
                onChange={(e) => setThresholdMm(parseInt(e.target.value, 10))}
                onBlur={() => {
                  if (thresholdMm !== existing.threshold_mismatches) {
                    upsert({ threshold_mismatches: thresholdMm });
                  }
                }}
                data-testid="drift-watch-threshold-mismatches"
                className="w-14 rounded border border-black/10 px-1.5 py-0.5 text-center"
              />
            </label>
            {existing?.last_alert_at && (
              <span className="text-ek-ink/45" data-testid="drift-watch-last-alert">
                · last alert {new Date(existing.last_alert_at).toLocaleString()}
                {(existing.alert_count || 0) > 0 && ` · ${existing.alert_count} total`}
              </span>
            )}
          </>
        )}
      </div>
      {watched && (
        <p className="mt-1.5 text-[10.5px] text-ek-ink/55">
          ERIO checks this pair every hour. Alerts go to the tenant CRO with the
          full PDF attached. Dedup: 24h cooldown or delta worsened by ≥ 0.1.
        </p>
      )}
    </div>
  );
}


function DriftSection({ programmeId }) {
  const [connectors, setConnectors] = useState([]);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [watches, setWatches] = useState([]);

  const loadWatches = useCallback(async () => {
    try {
      const { data } = await api.get(`/programmes/${programmeId}/drift-watches`);
      setWatches(data.items || []);
    } catch { /* viewer roles see nothing */ }
  }, [programmeId]);

  useEffect(() => {
    api.get("/connectors").then(({ data: r }) => {
      // Keep all configured connectors visible — even when last_sync_count is
      // 0 (e.g. a hibernating ServiceNow PDI mid-tick). The FreshnessBadge
      // surfaces staleness so users can still see the connector is configured
      // and choose to retry. Previously we filtered last_sync_count>0 which
      // silently hid hibernating connectors from the picker.
      const configured = (r.items || []).filter((c) => c.status !== "unconfigured");
      setConnectors(configured);
      // Prefer a connector that has data ready; fall back to the first.
      const ready = configured.find((c) => (c.last_sync_count || 0) > 0);
      if (configured.length > 0) setPick((ready || configured[0]).connector_id);
    }).catch(() => { /* viewer roles see nothing */ });
    loadWatches();
  }, [loadWatches]);

  const compare = async () => {
    if (!pick) return;
    setBusy(true);
    try {
      const { data: r } = await api.post(`/programmes/${programmeId}/compare-with-connector`, {
        connector_id: pick,
      });
      setReport(r);
      const delta = r.drift.overall_score_delta;
      toast.success(`Drift report: ${r.drift.matched_count} matched · ${r.drift.uploaded_only_count} plan-only · ${r.drift.connector_only_count} live-only · Δ${delta >= 0 ? "+" : ""}${delta.toFixed(2)}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Compare failed");
    } finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    if (!pick) return;
    setBusy("pdf");
    try {
      const res = await api.post(
        `/programmes/${programmeId}/drift-report.pdf`,
        { connector_id: pick },
        { responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const slug = (report?.programme?.name || "programme").replace(/\s+/g, "-").toLowerCase().slice(0, 40);
      a.download = `ERIO-drift-${slug}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Drift report PDF downloaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "PDF generation failed");
    } finally { setBusy(false); }
  };

  const emailToCro = async () => {
    if (!pick) return;
    const override = window.prompt(
      "Email drift report — recipient address (leave blank to send to the tenant CRO):",
      ""
    );
    if (override === null) return;  // user cancelled
    setBusy("email");
    try {
      const body = { connector_id: pick };
      if (override.trim()) body.recipient = override.trim();
      const { data } = await api.post(`/programmes/${programmeId}/drift-report/email`, body);
      const sent = data.resend?.sent;
      if (sent) {
        toast.success(`Drift report sent to ${data.recipient}`);
      } else {
        toast.error(`Email did not send: ${data.resend?.error || "unknown error"}`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Email failed");
    } finally { setBusy(false); }
  };

  return (
    <section className="mt-8 rounded-xl bg-white p-5 ring-1 ring-slate-200" data-testid="programme-drift-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-violet-900 flex items-center gap-2">
          <GitCompareArrows size={14} /> Compare with live connector data
        </h2>
        {connectors.length > 0 && (
          <div className="flex items-center gap-2">
            <select value={pick} onChange={(e) => setPick(e.target.value)}
              data-testid="drift-connector-select"
              className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs">
              {connectors.map((c) => (
                <option key={c.connector_id} value={c.connector_id}>
                  {c.name} — {c.last_sync_count} plan{c.last_sync_count === 1 ? "" : "s"}
                </option>
              ))}
            </select>
            <button onClick={compare} disabled={busy || !pick}
              className="inline-flex items-center gap-1.5 rounded-md bg-violet-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-800 disabled:opacity-50"
              data-testid="drift-run-btn">
              {busy === true ? "Comparing…" : "Run drift report"}
            </button>
            {report && (
              <>
                <button onClick={downloadPdf} disabled={busy !== false}
                  className="inline-flex items-center gap-1.5 rounded-md border border-violet-300 bg-white px-3 py-1.5 text-xs font-semibold text-violet-800 hover:bg-violet-50 disabled:opacity-50"
                  data-testid="drift-pdf-btn"
                  title="Download a one-page PDF of this drift report">
                  {busy === "pdf" ? "Generating…" : "Download PDF"}
                </button>
                <button onClick={emailToCro} disabled={busy !== false}
                  className="inline-flex items-center gap-1.5 rounded-md border border-violet-300 bg-white px-3 py-1.5 text-xs font-semibold text-violet-800 hover:bg-violet-50 disabled:opacity-50"
                  data-testid="drift-email-btn"
                  title="Email this drift report to the CRO (or override the recipient)">
                  {busy === "email" ? "Sending…" : "Email to CRO"}
                </button>
              </>
            )}
          </div>
        )}
      </div>
      {pick && <FreshnessBadge connectors={connectors} pick={pick} />}
      {connectors.length === 0 && (
        <p className="mt-3 text-xs text-ek-ink/60" data-testid="drift-connector-empty">
          Connect a PPM source under <Link to="/connectors" className="text-violet-700 hover:underline">PPM connectors</Link> and run a sync first.
        </p>
      )}
      {pick && (
        <DriftWatchControl
          programmeId={programmeId}
          connectorId={pick}
          watches={watches}
          onChanged={loadWatches}
        />
      )}
      {report && (
        <div className="mt-4 space-y-3" data-testid="drift-report">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="rounded-lg border border-slate-200 p-3">
              <p className="text-[10px] uppercase tracking-wide text-ek-ink/60">Plan-of-record (saved)</p>
              <p className="mt-1 font-display text-base font-bold text-ek-ink">L{report.programme.portfolio.overall_level} · {report.programme.portfolio.overall_score?.toFixed(2)}</p>
              <p className="text-ek-ink/55">{report.programme.total_items} item{report.programme.total_items === 1 ? "" : "s"}</p>
            </div>
            <div className="rounded-lg border border-violet-200 bg-violet-50 p-3">
              <p className="text-[10px] uppercase tracking-wide text-violet-700 flex items-center gap-1"><Plug size={10} /> Live ({report.connector.name})</p>
              <p className="mt-1 font-display text-base font-bold text-ek-ink">L{report.connector.portfolio.overall_level} · {report.connector.portfolio.overall_score?.toFixed(2)}</p>
              <p className="text-ek-ink/55">{report.connector.total_items} item{report.connector.total_items === 1 ? "" : "s"}</p>
            </div>
          </div>
          <p className={`text-sm font-semibold ${report.drift.overall_score_delta >= 0 ? "text-emerald-700" : "text-rose-700"}`}
             data-testid="drift-overall-delta">
            Overall drift Δ {report.drift.overall_score_delta >= 0 ? "+" : ""}{report.drift.overall_score_delta.toFixed(3)}
          </p>
          <div className="grid grid-cols-3 gap-3 text-xs">
            <Stat label="Matched" value={report.drift.matched_count} testid="drift-stat-matched" />
            <Stat label="Plan-of-record only" value={report.drift.uploaded_only_count} testid="drift-stat-uploaded-only" />
            <Stat label="Live only (in Jira)" value={report.drift.connector_only_count} testid="drift-stat-connector-only" />
          </div>
          {report.connector.financials && (
            <div data-testid="drift-financials" className="rounded-lg border border-rose-200 bg-rose-50/40 p-3">
              <p className="text-[10px] uppercase tracking-wide text-rose-700">£ exposure — quantified drift</p>
              <div className="mt-2 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-ek-ink/55 text-xs">Live portfolio value</p>
                  <p className="font-display text-lg font-bold text-ek-ink" data-testid="drift-total-budget">
                    {formatMoney(report.connector.financials.total_budget, report.connector.financials.currency)}
                  </p>
                </div>
                <div>
                  <p className="text-ek-ink/55 text-xs">Value at drift risk</p>
                  <p className="font-display text-lg font-bold text-rose-700" data-testid="drift-budget-at-risk">
                    {formatMoney(report.connector.financials.budget_at_risk, report.connector.financials.currency)}
                    {report.connector.financials.total_budget > 0 && (
                      <span className="ml-2 text-xs font-normal text-rose-600/80">
                        ({Math.round(100 * report.connector.financials.budget_at_risk / report.connector.financials.total_budget)}%)
                      </span>
                    )}
                  </p>
                </div>
              </div>
              {report.connector.financials.currency_mixed && (
                <p className="mt-2 text-[11px] text-amber-700">⚠ Mixed currencies in upstream — figures are unconverted sums.</p>
              )}
            </div>
          )}
          {report.connector.native_health && (
            <div data-testid="drift-native-health" className="rounded-lg border border-slate-200 p-3">
              <p className="text-[10px] uppercase tracking-wide text-ek-ink/65">Native PPM health vs ERIO PIS</p>
              <div className="mt-2 flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <span className="text-ek-ink/60">ServiceNow says:</span>
                  <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-semibold text-emerald-800" data-testid="native-green">{report.connector.native_health.green} green</span>
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-800" data-testid="native-amber">{report.connector.native_health.amber} amber</span>
                  <span className="rounded-full bg-rose-100 px-2 py-0.5 font-semibold text-rose-800" data-testid="native-red">{report.connector.native_health.red} red</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-ek-ink/60">ERIO PIS:</span>
                  <span className="rounded-full bg-violet-100 px-2 py-0.5 font-semibold text-violet-800">
                    {report.connector.portfolio.overall_score?.toFixed(2)} ({report.connector.portfolio.rag_bucket || "—"})
                  </span>
                </div>
              </div>
            </div>
          )}
          {report.drift.status_mismatches.length > 0 && (
            <div data-testid="drift-status-mismatches">
              <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-amber-700">Status mismatches</p>
              <table className="mt-1 w-full text-left text-xs">
                <thead className="text-[10px] uppercase tracking-wide text-ek-ink/55">
                  <tr><th className="py-1 pr-2">ID</th><th className="py-1 pr-2">Title</th><th className="py-1 pr-2">Saved</th><th className="py-1 pr-2">Live</th><th className="py-1">Source</th></tr>
                </thead>
                <tbody>
                  {report.drift.status_mismatches.map((m) => (
                    <tr key={m.plan_id} className="border-t border-slate-100">
                      <td className="py-1 pr-2 font-mono">{m.plan_id}</td>
                      <td className="py-1 pr-2">{m.title}</td>
                      <td className="py-1 pr-2"><span className="rounded-full bg-slate-100 px-1.5 py-0.5">{m.saved_status}</span></td>
                      <td className="py-1 pr-2"><span className="rounded-full bg-violet-100 px-1.5 py-0.5 text-violet-800">{m.live_status}</span></td>
                      <td className="py-1">{m.external_url && <a href={m.external_url} target="_blank" rel="noreferrer" className="text-violet-700 hover:underline inline-flex items-center gap-0.5"><ExternalLink size={9} /> open</a>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

const Stat = ({ label, value, testid }) => (
  <div className="rounded-lg border border-black/5 bg-ek-canvas/40 p-2 text-center" data-testid={testid}>
    <p className="text-[10px] uppercase tracking-wide text-ek-ink/65">{label}</p>
    <p className="mt-1 font-display text-base font-bold text-ek-ink">{value}</p>
  </div>
);

// Board-deck money formatter. Mirrors the backend `_money_str` helper:
// £3.20m / £45k / £750 — consistent with the PDF drift report so screen
// and paper read identically.
const CURRENCY_SYMBOL = { GBP: "£", USD: "$", EUR: "€" };
function formatMoney(amount, currency) {
  if (amount == null) return "—";
  const sym = CURRENCY_SYMBOL[(currency || "").toUpperCase()] || "";
  const abs = Math.abs(amount);
  let body;
  if (abs >= 1_000_000) body = `${(amount / 1_000_000).toFixed(2)}m`;
  else if (abs >= 1_000) body = `${Math.round(amount / 1_000)}k`;
  else body = amount.toLocaleString();
  return sym ? `${sym}${body}` : `${body} ${currency || ""}`.trim();
}
